/**
 * MODULE: domain/pages/frontend/utils/crmMentionUtils.ts
 * PURPOSE: Token format and helpers for CRM @mentions in page editor blocks
 */

export type CrmMentionEntityType = 'contact' | 'company' | 'deal';

export type CrmMentionHit = {
  id: string;
  entity_type: CrmMentionEntityType;
  label: string;
  subtitle?: string | null;
};

/** Persisted token: @[entityType:uuid:Label] */
export const CRM_MENTION_TOKEN_PATTERN =
  /@\[([a-z]+):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):([^\]]+)\]/gi;

const ENTITY_TYPES = new Set<CrmMentionEntityType>(['contact', 'company', 'deal']);

export function buildCrmMentionToken(
  entityType: CrmMentionEntityType,
  id: string,
  label: string
): string {
  return `@[${entityType}:${id}:${label.replace(/\]/g, '')}]`;
}

export function getCrmMentionMatch(
  textBefore: string
): { query: string; atIndex: number; length: number } | null {
  const atIndex = textBefore.lastIndexOf('@');
  if (atIndex === -1) return null;
  if (atIndex > 0 && !/\s/.test(textBefore[atIndex - 1] ?? '')) {
    return null;
  }
  const query = textBefore.slice(atIndex + 1);
  if (!query.length || query.includes(' ') || query.includes('[')) {
    return query.length === 0 ? { query: '', atIndex, length: 1 } : null;
  }
  return { query, atIndex, length: textBefore.length - atIndex };
}

type InlineTipTapNode = {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
};

export function textWithTokensToInlineNodes(text: string): InlineTipTapNode[] {
  const nodes: InlineTipTapNode[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(CRM_MENTION_TOKEN_PATTERN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      nodes.push({ type: 'text', text: text.slice(lastIndex, start) });
    }
    const entityType = match[1] as CrmMentionEntityType;
    const id = match[2] ?? '';
    const label = match[3] ?? '';
    if (ENTITY_TYPES.has(entityType) && id) {
      nodes.push({
        type: 'crmMention',
        attrs: { id, entityType, label },
      });
    } else {
      nodes.push({ type: 'text', text: match[0] });
    }
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push({ type: 'text', text: text.slice(lastIndex) });
  }
  return nodes.length > 0 ? nodes : [];
}

export function inlineNodesToText(nodes: InlineTipTapNode[] | undefined): string {
  if (!nodes?.length) return '';
  return nodes
    .map((node) => {
      if (node.type === 'text') return node.text ?? '';
      if (node.type === 'crmMention') {
        const entityType = String(node.attrs?.entityType ?? 'contact') as CrmMentionEntityType;
        const id = String(node.attrs?.id ?? '');
        const label = String(node.attrs?.label ?? 'Untitled');
        if (!id) return `@${label}`;
        return buildCrmMentionToken(entityType, id, label);
      }
      return '';
    })
    .join('');
}

export function crmMentionPath(entityType: CrmMentionEntityType, id: string): string {
  switch (entityType) {
    case 'contact':
      return `/crm/contacts/${encodeURIComponent(id)}`;
    case 'company':
      return `/accounts/${encodeURIComponent(id)}`;
    case 'deal':
      return `/crm/deals/${encodeURIComponent(id)}`;
    default:
      return '/crm';
  }
}

export const CRM_MENTION_CHIP_CLASS: Record<CrmMentionEntityType, string> = {
  contact: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  company: 'bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200',
  deal: 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200',
};
